<template>
  <div class="detalle-overlay" @click.self="cerrar">
    <div class="detalle-caja" role="dialog" aria-modal="true" :aria-label="`Detalle del código ${codigo}`">
      <!-- Encabezado -->
      <div class="detalle-header">
        <div>
          <div class="small text-muted">Detalle del tarifario</div>
          <h5 class="mb-0">Código {{ codigo }}</h5>
        </div>
        <button type="button" class="btn-close" aria-label="Cerrar" @click="cerrar"></button>
      </div>

      <!-- Contenido -->
      <div class="detalle-body">
        <div v-if="cargando" class="text-center my-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
        </div>

        <div v-else-if="error" class="alert alert-danger mb-0" role="alert">{{ error }}</div>

        <div v-else-if="filas.length === 0" class="alert alert-warning mb-0" role="alert">
          El código <strong>{{ codigo }}</strong> no existe en el tarifario.
        </div>

        <template v-else>
          <!-- Un bloque por tipo de procedimiento (HM, MDD...) -->
          <div v-for="f in filas" :key="f.codigo + '|' + f.tipoProcedimiento" class="card mb-3">
            <div class="card-header d-flex flex-wrap align-items-center gap-2">
              <span class="badge bg-dark">{{ f.tipoProcedimiento }}</span>
              <span class="badge" :class="claseEstado(f.estado)">{{ (f.estado || "—").trim() }}</span>
              <span class="fw-semibold">{{ f.nombre }}</span>
            </div>

            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-sm table-bordered mb-0 align-middle">
                  <thead class="table-light">
                    <tr>
                      <th>Nivel</th>
                      <th class="text-end">UVR</th>
                      <th class="text-end">FCM</th>
                      <th class="text-end">Valor</th>
                      <th class="text-end">UVR anes.</th>
                      <th class="text-end">FCM anes.</th>
                      <th class="text-end">Valor anes.</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="n in niveles(f)"
                      :key="n.nivel"
                      :class="{ 'table-primary': n.numero === nivel }"
                    >
                      <td class="fw-semibold">
                        Nivel {{ n.nivel }}
                        <span v-if="n.numero === nivel" class="badge bg-primary ms-1">prestador</span>
                      </td>
                      <td class="text-end">{{ formatoNumero(n.uvr) }}</td>
                      <td class="text-end">{{ formatoNumero(n.fcm) }}</td>
                      <td class="text-end fw-bold">{{ formatoMoneda(n.valor) }}</td>
                      <td class="text-end">{{ formatoNumero(n.uvrAnes) }}</td>
                      <td class="text-end">{{ formatoNumero(n.fcmAnes) }}</td>
                      <td class="text-end fw-bold">{{ formatoMoneda(n.valorAnes) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="card-footer small d-flex flex-wrap gap-3">
              <span>
                Incremento Costa:
                <strong>{{ f.incrementoCosta ? "Sí" : "No" }}</strong>
              </span>
              <span>
                Incremento Galápagos:
                <strong>{{ f.incrementoGalapagos ? "Sí" : "No" }}</strong>
              </span>
              <span class="text-muted ms-auto">Cargado: {{ formatoFecha(f.fechaCarga) }}</span>
            </div>
          </div>
        </template>
      </div>

      <!-- Pie -->
      <div class="detalle-footer">
        <button type="button" class="btn btn-secondary" @click="cerrar">Cerrar</button>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";

export default {
  name: "DetalleTarifarioModal",
  props: {
    // Código a consultar en /tpsns-detalle/codigo/{codigo}
    codigo: { type: String, required: true },
    // Nivel del prestador (1, 2 o 3) para resaltar su fila. Opcional.
    nivel: { type: Number, default: null },
  },
  emits: ["cerrar"],
  data() {
    return {
      filas: [],
      cargando: false,
      error: "",
      secuencia: 0,
    };
  },
  watch: {
    codigo: {
      immediate: true,
      handler() { this.cargar(); },
    },
  },
  mounted() {
    window.addEventListener("keydown", this.onTecla);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onTecla);
  },
  methods: {
    cerrar() {
      this.$emit("cerrar");
    },
    onTecla(e) {
      if (e.key === "Escape") this.cerrar();
    },
    async cargar() {
      const seq = ++this.secuencia;
      this.cargando = true;
      this.error = "";
      this.filas = [];
      try {
        const res = await api.get(`/tpsns-detalle/codigo/${encodeURIComponent(this.codigo)}`);
        if (seq !== this.secuencia) return;
        this.filas = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        if (seq !== this.secuencia) return;
        // 404 = el código no existe en el tarifario: se muestra el aviso, no un error
        if (e.response?.status !== 404) {
          console.error("Error cargando el detalle del tarifario:", e);
          this.error = "No se pudo cargar el detalle del tarifario.";
        }
      } finally {
        if (seq === this.secuencia) this.cargando = false;
      }
    },
    // Reorganiza las columnas del registro en una fila por nivel
    niveles(f) {
      return [
        { numero: 1, nivel: "I",   uvr: f.uvrI,   fcm: f.fcmI,   valor: f.valorI,
          uvrAnes: f.uvrAnesI,   fcmAnes: f.fcmAnesI,   valorAnes: f.valorAnesI },
        { numero: 2, nivel: "II",  uvr: f.uvrIi,  fcm: f.fcmIi,  valor: f.valorIi,
          uvrAnes: f.uvrAnesIi,  fcmAnes: f.fcmAnesIi,  valorAnes: f.valorAnesIi },
        { numero: 3, nivel: "III", uvr: f.uvrIii, fcm: f.fcmIii, valor: f.valorIii,
          uvrAnes: f.uvrAnesIii, fcmAnes: f.fcmAnesIii, valorAnes: f.valorAnesIii },
      ];
    },
    claseEstado(estado) {
      return (estado || "").trim().toUpperCase() === "ACTIVO" ? "bg-success" : "bg-secondary";
    },
    formatoMoneda(v) {
      if (v === null || v === undefined) return "—";
      return Number(v).toLocaleString("es-EC", { style: "currency", currency: "USD" });
    },
    formatoNumero(v) {
      if (v === null || v === undefined) return "—";
      return Number(v).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });
    },
    formatoFecha(f) {
      if (!f) return "—";
      const d = new Date(f);
      return isNaN(d) ? f : d.toLocaleString("es-EC");
    },
  },
};
</script>

<style scoped>
.detalle-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.detalle-caja {
  background: #fff;
  border-radius: 0.5rem;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.3);
}
.detalle-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #dee2e6;
}
.detalle-body {
  padding: 1rem 1.25rem;
  overflow-y: auto;
}
.detalle-footer {
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #dee2e6;
  text-align: right;
}
</style>